import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { environment } from '@environments/environment';
import { SKIP_ERROR_TOAST } from '@core/http/http-context.tokens';
import { IndustryType } from '@shared/models/industry-type.model';
import { resolveHttpErrorMessage } from '@core/utils/http-error.util';
import { IndustryContextService } from './industry-context.service';
import { LocalStorageService } from './local-storage.service';
import { AuthTokenService } from './auth-token.service';
import { UserSessionService } from './user-session.service';
import {
  mapSidebarResponse,
  moduleRouteFromUrl,
  SIDEBAR_SESSION_CACHE_KEY,
  toSidebarNavItems,
  type SidebarPayload,
} from '../navigation/sidebar-module-catalog';

interface SidebarCacheRecord {
  scope: string;
  payload: SidebarPayload;
}

export type SidebarLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

function industryQueryValue(industry: IndustryType | null): string | null {
  if (industry == null) {
    return null;
  }
  const name = IndustryType[industry];
  return typeof name === 'string' && name.length > 0 ? name : null;
}

function isSidebarCacheRecord(value: unknown): value is SidebarCacheRecord {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Partial<SidebarCacheRecord>;
  return typeof record.scope === 'string' && record.payload != null && typeof record.payload === 'object';
}

/**
 * Session-scoped CRM sidebar. The server decides which modules exist for the
 * current user, role, industry, and tenant. This client maps keys to routes.
 */
@Injectable({ providedIn: 'root' })
export class SidebarModulesService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(UserSessionService);
  private readonly tokens = inject(AuthTokenService);
  private readonly industryCtx = inject(IndustryContextService);
  private readonly storage = inject(LocalStorageService);
  private readonly baseUrl = environment.apiBaseUrl;

  private readonly modulesState = signal<SidebarPayload['modules']>([]);
  private readonly industryState = signal<SidebarPayload['industry']>(null);
  private readonly appliedScope = signal<string | null>(null);
  private readonly statusState = signal<SidebarLoadStatus>('idle');
  private readonly errorState = signal<string | null>(null);

  private generation = 0;
  private load$: Observable<void> | null = null;
  private loadScope = '';

  readonly modules = this.modulesState.asReadonly();
  readonly industry = this.industryState.asReadonly();
  readonly status = this.statusState.asReadonly();
  readonly errorMessage = this.errorState.asReadonly();
  readonly navItems = computed(() => toSidebarNavItems(this.modulesState()));
  readonly homeRoute = computed(() => this.navItems()[0]?.route ?? null);

  constructor() {
    effect(() => {
      const userId = this.session.user()?.id?.trim() ?? '';
      const token = this.tokens.accessToken();
      if (!userId || !token) {
        this.clear();
      }
    });
  }

  isRouteAllowed(route: string): boolean {
    return this.navItems().some((item) => item.route === route);
  }

  labelForUrl(url: string): string | null {
    const route = moduleRouteFromUrl(url);
    if (!route) {
      return null;
    }
    return this.navItems().find((item) => item.route === route)?.label ?? null;
  }

  /** i18n key for the module root at `url`, when the sidebar knows that route. */
  labelKeyForUrl(url: string): string | null {
    const route = moduleRouteFromUrl(url);
    if (!route) {
      return null;
    }
    return this.navItems().find((item) => item.route === route)?.labelKey ?? null;
  }

  /**
   * Loads the sidebar for the signed-in scope. Session cache is reused until
   * `force` is set (login refresh is a cache miss; tenant switch passes force).
   */
  ensureLoaded(options?: { force?: boolean }): Observable<void> {
    if (!this.tokens.accessToken() || !this.session.user()?.id) {
      this.clear();
      return of(void 0);
    }

    const force = options?.force === true;
    const scope = this.scopeKey();

    if (!force && this.statusState() === 'ready' && this.appliedScope() === scope) {
      return of(void 0);
    }

    if (!force && this.load$ && this.loadScope === scope) {
      return this.load$;
    }

    if (!force) {
      const cached = this.readCache(scope);
      if (cached) {
        this.apply(scope, cached);
        return of(void 0);
      }
    }

    const generation = ++this.generation;
    const hadCurrentPayload = this.appliedScope() === scope;
    if (!hadCurrentPayload) {
      this.modulesState.set([]);
      this.industryState.set(null);
      this.appliedScope.set(null);
    }
    this.statusState.set('loading');
    this.errorState.set(null);
    this.loadScope = scope;

    let params = new HttpParams();
    const industry = industryQueryValue(this.industryCtx.industry());
    if (industry) {
      params = params.set('industry', industry);
    }

    const request$ = this.http
      .get<unknown>(`${this.baseUrl}/api/modules/sidebar`, {
        params,
        context: new HttpContext().set(SKIP_ERROR_TOAST, true),
      })
      .pipe(
        map((raw) => mapSidebarResponse(raw)),
        tap((payload) => {
          if (generation !== this.generation || this.scopeKey() !== scope) {
            return;
          }
          this.apply(scope, payload);
          this.writeCache(scope, payload);
        }),
        map(() => void 0),
        catchError((error: unknown) => {
          if (generation !== this.generation || this.scopeKey() !== scope) {
            return of(void 0);
          }
          const message =
            error instanceof HttpErrorResponse
              ? resolveHttpErrorMessage(error)
              : 'Unable to load navigation.';
          this.errorState.set(message);
          this.statusState.set(hadCurrentPayload ? 'ready' : 'error');
          return of(void 0);
        }),
        finalize(() => {
          if (this.load$ === request$) {
            this.load$ = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.load$ = request$;
    return request$;
  }

  /** Refetch after a display-name save when this session is previewing that industry. */
  reloadIfSameIndustry(code: number, name: string): void {
    const current = this.industryState();
    if (!current) {
      return;
    }
    const sameCode = current.code === code;
    const sameName =
      current.name.trim().length > 0 &&
      current.name.trim().toLowerCase() === name.trim().toLowerCase();
    if (!sameCode && !sameName) {
      return;
    }
    this.ensureLoaded({ force: true }).subscribe();
  }

  private apply(scope: string, payload: SidebarPayload): void {
    this.modulesState.set(payload.modules);
    this.industryState.set(payload.industry);
    this.appliedScope.set(scope);
    this.statusState.set('ready');
    this.errorState.set(null);
  }

  private clear(): void {
    this.generation += 1;
    this.load$ = null;
    this.loadScope = '';
    this.modulesState.set([]);
    this.industryState.set(null);
    this.appliedScope.set(null);
    this.statusState.set('idle');
    this.errorState.set(null);
    this.storage.removeItem('session', SIDEBAR_SESSION_CACHE_KEY);
  }

  private scopeKey(): string {
    const user = this.session.user();
    const userId = user?.id?.trim() ?? '';
    const tenantId = user?.tenantId?.trim() ?? '';
    const industry = industryQueryValue(this.industryCtx.industry()) ?? '';
    return `${userId}|${tenantId}|${industry}`;
  }

  private readCache(scope: string): SidebarPayload | null {
    const cached = this.storage.readJson<SidebarCacheRecord>('session', SIDEBAR_SESSION_CACHE_KEY, {
      validate: isSidebarCacheRecord,
    });
    if (!cached || cached.scope !== scope) {
      return null;
    }
    return mapSidebarResponse(cached.payload);
  }

  private writeCache(scope: string, payload: SidebarPayload): void {
    this.storage.writeJson('session', SIDEBAR_SESSION_CACHE_KEY, {
      scope,
      payload,
    } satisfies SidebarCacheRecord);
  }
}
